import { useCallback, useMemo } from "react"
import { toast } from "react-toastify"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import {
  useAudienceClassificationControllerGetAllQuery,
  useAudienceClassificationControllerReplaceAllMutation,
} from "~/ui-rtk/api/audienceClassificationApi"

export type TAudienceOption = {
  id: string
  name: string
  totalExcludedSpend: number
  adsetCountExcluded: number
  subtype: string | null
}

export type TAudiencesFormValues = {
  engaged: string[]
  existingCustomer: string[]
}

export default function useConnect() {
  const { data: classifications, isLoading: isLoadingClassifications } =
    useAudienceClassificationControllerGetAllQuery()
  const [replaceAll, { isLoading: isSaving }] =
    useAudienceClassificationControllerReplaceAllMutation()

  const initialValues: TAudiencesFormValues = useMemo(() => {
    if (!classifications) return { engaged: [], existingCustomer: [] }
    return {
      engaged: classifications
        .filter(c => c.classification === "ENGAGED")
        .map(c => c.audienceRef),
      existingCustomer: classifications
        .filter(c => c.classification === "EXISTING")
        .map(c => c.audienceRef),
    }
  }, [classifications])

  // Fetch custom audiences from Cube.js
  const { resultSet, isLoading: isLoadingAudiences } = useCubeQuery({
    dimensions: [
      "company_custom_audiences.audience_id",
      "company_custom_audiences.audience_name",
      "company_custom_audiences.subtype",
    ],
    measures: [
      "company_custom_audiences.total_excluded_spend",
      "company_custom_audiences.adset_count_excluded",
    ],
    order: {
      "company_custom_audiences.total_excluded_spend": "desc",
    },
  })

  const audienceOptions: TAudienceOption[] = useMemo(() => {
    if (!resultSet) return []
    return (resultSet.rawData() || []).map((row: Record<string, unknown>) => ({
      id: String(row["company_custom_audiences.audience_id"] ?? ""),
      name: String(row["company_custom_audiences.audience_name"] ?? ""),
      totalExcludedSpend: Number(
        row["company_custom_audiences.total_excluded_spend"] ?? 0,
      ),
      adsetCountExcluded: Number(
        row["company_custom_audiences.adset_count_excluded"] ?? 0,
      ),
      subtype: row["company_custom_audiences.subtype"] as string | null,
    }))
  }, [resultSet])

  const updateAudiences = useCallback(
    async (values: TAudiencesFormValues) => {
      const classifications = [
        ...values.engaged.map(audienceRef => ({
          audienceRef,
          classification: "ENGAGED" as const,
        })),
        ...values.existingCustomer.map(audienceRef => ({
          audienceRef,
          classification: "EXISTING" as const,
        })),
      ]

      try {
        await replaceAll({ classifications }).unwrap()
        toast.success("Audiences updated successfully")
      } catch (error) {
        console.error(error)
        toast.error("Could not update audiences")
      }
    },
    [replaceAll],
  )

  return {
    initialValues,
    audienceOptions,
    isLoadingAudiences: isLoadingAudiences || isLoadingClassifications,
    isSaving,
    updateAudiences,
  }
}
